/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-25 22:25:50
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Button, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderLabelTooltip } from 'src/components/views/common/forms/render-label-tooltip';
import EditModal from 'src/components/containers/tools/custom-sorting/default-sorting-options/specific-collections-modal';
import NoSort from './no-sort';
import TableRow from './table-row';
import LoadingRows from './loading-rows';
import ConfirmDeleteModal from './confirm-delete-modal';

type Props = {
  fields: Array<Object>,
  loading: boolean,
  success: boolean,
  handleActiveModalEdit: () => void
};

type ComposedProps = Props & WithI18nProps;

type State = {
  activeModalEdit: boolean,
  activeModalRemove: boolean,
  selectedId: number
};

class SpecificCollections extends Component<ComposedProps, State> {
  state = {
    activeModalEdit: false,
    activeModalRemove: false,
    selectedId: -1
  };

  render() {
    const { i18n, specificCollections, optionSorts, loading, disabledAddNew, disabledActions } =
      this.props;
    const { selectedId, activeModalEdit, activeModalRemove } = this.state;

    return (
      <>
        <RenderLabelTooltip
          label={i18n.translate(
            'AdminHTML.CustomSorting.DefaultSortingOptions.specificCollections'
          )}
          tooltip={i18n.translate(
            'AdminHTML.CustomSorting.DefaultSortingOptions.specificCollections_Tooltip',
            {
              t1: (
                <TextStyle variation="strong">
                  {i18n.translate(
                    'AdminHTML.CustomSorting.DefaultSortingOptions.specificCollections_Tooltip_t1'
                  )}
                </TextStyle>
              )
            }
          )}></RenderLabelTooltip>
        <div className="Polaris-DataTable BC-DataTable__CollectionDefaultSort">
          <div className="Polaris-DataTable__ScrollContainer" style={{ marginLeft: 0 }}>
            <table className="Polaris-DataTable__Table">
              <thead>
                <tr>
                  <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                    {i18n.translate('AdminHTML.CustomSorting.DefaultSortingOptions.sortingOption')}
                  </th>
                  <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                    {i18n.translate('AdminHTML.CustomSorting.DefaultSortingOptions.collections')}
                  </th>
                  <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--numeric">
                    {i18n.translate('AdminHTML.CustomSorting.DefaultSortingOptions.actions')}
                  </th>
                </tr>
              </thead>
              {!_.isEmpty(specificCollections) ? (
                <tbody>
                  {specificCollections.map((item, key) => {
                    return (
                      <TableRow
                        item={item}
                        index={key}
                        key={key}
                        optionSorts={optionSorts}
                        handleActiveModalEdit={this.handleActiveModalEdit}
                        updateSelected={this.updateSelected}
                        handleActiveOptionModalRemove={this.handleActiveOptionModalRemove}
                        disabled={disabledActions}
                      />
                    );
                  })}
                  {loading && (
                    // Loading rows while reading
                    <LoadingRows />
                  )}
                </tbody>
              ) : (
                <tbody>
                  {loading && (
                    // Loading rows while reading
                    <LoadingRows />
                  )}
                </tbody>
              )}
            </table>

            {/* No card */}
            {_.isEmpty(specificCollections) && !loading ? <NoSort /> : ''}
          </div>
          <div className="addNew">
            <Button plain onClick={() => this.handleActiveModalEdit(-1)} disabled={disabledAddNew}>
              {i18n.translate('AdminHTML.CustomSorting.DefaultSortingOptions.addNew')}
            </Button>
          </div>
        </div>
        {/* Confirm delete modal */}
        <ConfirmDeleteModal
          active={activeModalRemove}
          handleChange={this.handleActiveOptionModalRemove}
          handleRemove={this.deleteCustomSorting}
          title={i18n.translate(
            'AdminHTML.CustomSorting.DefaultSortingOptions.confirmDelete_Title'
          )}
        />

        {/* Create/Edit modal */}
        <EditModal
          active={activeModalEdit}
          index={selectedId}
          optionSorts={optionSorts}
          handleChange={this.handleActiveModalEdit}
          specificCollectionsItem={specificCollections[selectedId] || {}}
          specificCollections={specificCollections}
          updateSpecificCollectionsItem={this.updateSpecificCollectionsItem}
        />
      </>
    );
  }

  handleActiveModalEdit = id => {
    const { activeModalEdit } = this.state;
    if (activeModalEdit) {
      this.setState({ activeModalEdit: !activeModalEdit, selectedId: -1 });
    } else {
      this.setState({ activeModalEdit: !activeModalEdit, selectedId: id });
    }
  };

  // Update selected id
  updateSelected = id => {
    this.setState(({ selectedId }) => ({
      selectedId: id
    }));
  };

  // Show/Hide Remove modal
  handleActiveOptionModalRemove = () => {
    this.setState(({ activeModalRemove }) => ({
      activeModalRemove: !activeModalRemove
    }));
  };

  /**
   * Delete custom sorting item
   */
  deleteCustomSorting = () => {
    const { selectedId } = this.state;
    const { fields } = this.props;

    fields.remove(selectedId);
    this.setState({ selectedId: -1 });
    this.handleActiveOptionModalRemove();
  };

  updateSpecificCollectionsItem = (data, index) => {
    if (index === -1) {
      // New option sorting
      const { fields } = this.props;
      fields.push({ ...data });
    } else {
      // Update fields
      const { updateCustomSortingForm } = this.props;
      updateCustomSortingForm(`defaultSorting.specificCollections[${index}]`, data);
    }
  };
}

export default withI18n()(SpecificCollections);
